import { t } from '@lingui/core/macro';
import { type AppLinkOptions, linkOptions } from '@luminovo/design-system';

/** A group of pages. The settings sidebar picks its icon by `id`. */
export type SettingsGroup = {
    id: 'organization' | 'design' | 'customerPortal' | 'integrations' | 'account';
    label: string;
    pages: Array<{ label: string; link: AppLinkOptions }>;
};

/**
 * The settings' groups and pages, in the order of the new navigation's settings sidebar. Plain data, without the
 * icons: the redirect that opens the first page reads it too, and route options load at start-up.
 */
export function settingsMenu() {
    // `satisfies`, not an annotation: each link keeps its route literal for that redirect.
    return [
        {
            id: 'organization',
            label: t`Organization`,
            pages: [
                { label: t`Users`, link: linkOptions({ to: '/settings/organization/users' }) },
                { label: t`Roles`, link: linkOptions({ to: '/settings/organization/user-management/roles' }) },
                { label: t`Sites`, link: linkOptions({ to: '/settings/organization/sites' }) },
                { label: t`Branding`, link: linkOptions({ to: '/settings/organization/branding' }) },
                { label: t`Project numbers`, link: linkOptions({ to: '/settings/organization/automatic-rfq-number' }) },
                { label: t`Feature flags`, link: linkOptions({ to: '/settings/organization/feature-flags' }) },
            ],
        },
        {
            id: 'integrations',
            label: t`Integrations`,
            pages: [
                {
                    label: t`Supplier integrations`,
                    link: linkOptions({ to: '/settings/organization/supplier-api-integrations' }),
                },
                { label: t`Data integrations`, link: linkOptions({ to: '/settings/organization/data-integrations' }) },
                // The quota of the carbon-footprint data service. A user who may open Design settings gets this page
                // or Data integrations, whichever the `silicon_expert` flag selects.
                { label: t`Carbon footprints`, link: linkOptions({ to: '/settings/organization/carbon-footprints' }) },
                { label: t`CSV import`, link: linkOptions({ to: '/settings/organization/csv-integration' }) },
                { label: t`Luminovo API`, link: linkOptions({ to: '/settings/organization/api-integration' }) },
            ],
        },
        {
            id: 'design',
            label: t`Design`,
            pages: [
                { label: t`Assemblies`, link: linkOptions({ to: '/settings/organization/assembly-settings' }) },
                { label: t`Components`, link: linkOptions({ to: '/settings/organization/component-settings' }) },
                {
                    label: t`Manufacturers`,
                    link: linkOptions({ to: '/settings/organization/manufacturer-preferences' }),
                },
                {
                    label: t`Custom part categories`,
                    link: linkOptions({ to: '/settings/organization/custom-part-categories' }),
                },
                // The panel preference pages beneath it have no entry of their own, so this one stands for them.
                { label: t`PCB`, link: linkOptions({ to: '/settings/organization/pcb' }) },
            ],
        },
        {
            id: 'customerPortal',
            label: t`Customer portal`,
            pages: [
                { label: t`General`, link: linkOptions({ to: '/settings/organization/customer-portal' }) },
                {
                    label: t`Landing page`,
                    link: linkOptions({ to: '/settings/organization/customer-portal/landing-page' }),
                },
                {
                    label: t`Per customer`,
                    link: linkOptions({ to: '/settings/organization/customer-portal/customer-specific-settings' }),
                },
                {
                    label: t`Additional services`,
                    link: linkOptions({ to: '/settings/organization/customer-portal/additional-services' }),
                },
                {
                    label: t`Checkout`,
                    link: linkOptions({ to: '/settings/organization/customer-portal/checkout-conditions' }),
                },
                {
                    label: t`Payments`,
                    link: linkOptions({ to: '/settings/organization/customer-portal/payment-credentials' }),
                },
            ],
        },
        {
            id: 'account',
            label: t`Account`,
            pages: [{ label: t`Security`, link: linkOptions({ to: '/settings/user/account-security' }) }],
        },
    ] satisfies SettingsGroup[];
}
